import { MathUtils } from "three";

const { DEG2RAD, clamp } = MathUtils;

/** A WGS84 position as stored in the Dashboard project details */
export type GeoPosition = {
  lat: number;
  lng: number;
};

/** The basemaps available for the world map layer, as in the Dashboard */
export enum WorldMapStyle {
  map = "Map",
  satellite = "Satellite",
}

const EARTH_CIRCUMFERENCE_M = 40075016.686;
/** Edge length of the rendered map tiles, in pixels */
export const TILE_SIZE_PX = 256;
const MIN_ZOOM = 2;
/** Highest zoom level of the map imagery */
export const MAX_TILE_ZOOM = 19;

/** Number of tiles along each side of the tile grid of one zoom level */
export const WORLD_MAP_GRID_SIZE = 5;
/** Number of zoom levels around the view, each covering twice the area of the previous one */
export const WORLD_MAP_LEVELS = 9;

/** A single Web Mercator tile, placed relative to the project position */
export type WorldMapTile = {
  /** Unique per rendered tile, also across copies wrapped around the antimeridian */
  key: string;
  z: number;
  /** Tile column, wrapped into the valid range */
  x: number;
  y: number;
  /** Edge length of the tile in meters */
  size: number;
  /** Offset of the tile center towards east, in meters */
  east: number;
  /** Offset of the tile center towards north, in meters */
  north: number;
};

/**
 * @param position A WGS84 position
 * @param zoom The Web Mercator zoom level
 * @returns The fractional tile coordinates of the position
 */
export function geoToTile(
  { lat, lng }: GeoPosition,
  zoom: number,
): { x: number; y: number } {
  const n = 2 ** zoom;
  const latRad = lat * DEG2RAD;
  return {
    x: ((lng + 180) / 360) * n,
    y: ((1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2) * n,
  };
}

/**
 * @param position The map origin; the map is a Web Mercator plane scaled to meters at this latitude
 * @param zoom The Web Mercator zoom level
 * @returns The edge length of a tile at this zoom level, in meters
 */
export function tileSizeMeters(position: GeoPosition, zoom: number): number {
  return (EARTH_CIRCUMFERENCE_M * Math.cos(position.lat * DEG2RAD)) / 2 ** zoom;
}

/**
 * @param x Fractional tile column
 * @param y Fractional tile row
 * @param zoom The Web Mercator zoom level
 * @returns The WGS84 position of the tile coordinates
 */
export function tileToGeo(x: number, y: number, zoom: number): GeoPosition {
  const n = 2 ** zoom;
  return {
    lat: Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n))) / DEG2RAD,
    lng: (x / n) * 360 - 180,
  };
}

/**
 * @param origin The map origin (the project coordinates)
 * @param east Meters east of the origin, on the map plane
 * @param north Meters north of the origin, on the map plane
 * @returns The WGS84 position of the map point
 */
export function mapLocalToGeo(
  origin: GeoPosition,
  east: number,
  north: number,
): GeoPosition {
  const unit = tileSizeMeters(origin, 0);
  const tile = geoToTile(origin, 0);
  return tileToGeo(tile.x + east / unit, tile.y - north / unit, 0);
}

/**
 * Selects the tiles around the point the user looks at: a fine grid where the resolution matches
 * the screen, surrounded by coarser grids, so that the map extends as far as the user can see.
 *
 * @param position The map origin (the project coordinates)
 * @param focus The point the user looks at, in meters east and north of the position
 * @param metersPerPixel The size of a screen pixel at the focus point, in meters
 * @param style The basemap to use
 * @returns Non-overlapping tiles to render, with their offsets from the position in meters
 */
export function computeVisibleTiles(
  position: GeoPosition,
  focus: { east: number; north: number },
  metersPerPixel: number,
  style: WorldMapStyle,
): WorldMapTile[] {
  const fineZoom = clamp(
    Math.round(
      Math.log2(tileSizeMeters(position, 0) / (TILE_SIZE_PX * metersPerPixel)),
    ),
    MIN_ZOOM,
    MAX_TILE_ZOOM,
  );
  const coarseZoom = Math.max(MIN_ZOOM, fineZoom - WORLD_MAP_LEVELS + 1);
  const half = Math.floor(WORLD_MAP_GRID_SIZE / 2);

  // The grid of each level around the focus; column indices are not wrapped, to keep the geometry continuous
  const grids = new Map<number, Array<[number, number]>>();
  for (let z = coarseZoom; z <= fineZoom; z++) {
    const size = tileSizeMeters(position, z);
    const origin = geoToTile(position, z);
    const centerX = Math.floor(origin.x + focus.east / size);
    const centerY = Math.floor(origin.y - focus.north / size);
    const grid: Array<[number, number]> = [];
    for (let y = centerY - half; y <= centerY + half; y++) {
      for (let x = centerX - half; x <= centerX + half; x++) grid.push([x, y]);
    }
    grids.set(z, grid);
  }

  // Coarser tiles that contain finer ones are split, so that every area is drawn by exactly one level
  const toSplit = new Set<string>();
  for (let z = coarseZoom + 1; z <= fineZoom; z++) {
    for (const [x, y] of grids.get(z) ?? []) {
      for (let parent = z - 1; parent >= coarseZoom; parent--) {
        const factor = 2 ** (z - parent);
        toSplit.add(
          `${parent}/${Math.floor(x / factor)}/${Math.floor(y / factor)}`,
        );
      }
    }
  }

  const tiles: WorldMapTile[] = [];
  function cover(z: number, x: number, y: number): void {
    if (y < 0 || y >= 2 ** z) return;
    if (z < fineZoom && toSplit.has(`${z}/${x}/${y}`)) {
      for (const [dx, dy] of [
        [0, 0],
        [1, 0],
        [0, 1],
        [1, 1],
      ]) {
        cover(z + 1, 2 * x + dx, 2 * y + dy);
      }
      return;
    }
    tiles.push(createTile(position, style, z, x, y));
  }
  for (const [x, y] of grids.get(coarseZoom) ?? []) cover(coarseZoom, x, y);
  return tiles;
}

function createTile(
  position: GeoPosition,
  style: WorldMapStyle,
  z: number,
  x: number,
  y: number,
): WorldMapTile {
  const n = 2 ** z;
  const size = tileSizeMeters(position, z);
  const origin = geoToTile(position, z);
  const wrappedX = ((x % n) + n) % n;
  return {
    key: `${style}/${z}/${y}/${x}`,
    z,
    x: wrappedX,
    y,
    size,
    east: (x + 0.5 - origin.x) * size,
    north: (origin.y - y - 0.5) * size,
  };
}
